import React, { useState, useRef, useEffect } from 'react';
import { Button, Table, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Get, Post, PostFormData } from '../../../../utils/httpProvider';
import { ACCOUNT_API, ROLE_API, HOPDONGCHITIET_API } from '../../../../utils/apiUrl';

const TutorDepartmentCell = ({ accountId }) => {
    const [departmentName, setDepartmentName] = useState('Đang tải...');

    useEffect(() => {
        if (!accountId || accountId === '00000000-0000-0000-0000-000000000000') {
            setDepartmentName('Chưa liên kết tài khoản');
            return;
        }

        let isMounted = true;
        Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${accountId}`)
            .then(res => {
                if (isMounted) {
                    const donVis = res?.data?.donVis || res?.donVis;
                    if (donVis && donVis.length > 0) {
                        setDepartmentName(donVis.map(d => d.tenDonVi || d.TenDonVi).join(', '));
                    } else {
                        setDepartmentName(res?.data?.donViNgoaiVien || res?.donViNgoaiVien || 'Chưa cập nhật đơn vị');
                    }
                }
            })
            .catch(() => {
                if (isMounted) setDepartmentName('Lỗi tải đơn vị');
            });

        return () => {
            isMounted = false;
        };
    }, [accountId]);

    return <span>{departmentName}</span>;
};

const TutorTab = ({
    contractId,
    tutorsData,
    isTutorsLoading,
    refetchTutors
}) => {
    const tutorFileInputRef = useRef(null);

    // Modal state for manual Tutor creation
    const [showAddTutorModal, setShowAddTutorModal] = useState(false);
    const [newTutor, setNewTutor] = useState({ fullName: '', phoneNumber: '', dateOfBirth: '', identityCard: '' });

    const checkAccountExistsOnSso = async (username) => {
        if (!username) return null;
        try {
            const res = await Get(`${ACCOUNT_API.GET_PAGING_ACCOUNT}?Keyword=${username}`);
            const items = res?.data?.$values || res?.data || [];
            const found = items.find(acc => {
                const accUsername = acc.username || acc.Username || acc.userName || acc.UserName;
                return accUsername && accUsername.toLowerCase() === username.toLowerCase();
            });
            return found ? (found.id || found.Id) : null;
        } catch (err) {
            return null;
        }
    };

    // --- MANUAL TUTOR ACTIONS ---
    const handleOpenAddTutor = () => {
        setNewTutor({ fullName: '', phoneNumber: '', dateOfBirth: '', identityCard: '' });
        setShowAddTutorModal(true);
    };

    const handleSaveTutor = async () => {
        if (!newTutor.fullName || !newTutor.identityCard) {
            toast.warning("Họ tên và Số CCCD là bắt buộc.");
            return;
        }
        try {
            const searchKey = newTutor.identityCard;
            const resolvedId = await checkAccountExistsOnSso(searchKey);
            if (!resolvedId) {
                toast.error("Không tìm thấy tài khoản Giảng viên này trên hệ thống SSO. Vui lòng kiểm tra lại số CCCD.");
                return;
            }

            await Post(`${HOPDONGCHITIET_API.ADD_GIANGVIEN_MANUALLY}/${contractId}`, null, resolvedId);
            toast.success("Thêm giảng viên thành công!");
            setShowAddTutorModal(false);
            refetchTutors();
        } catch (err) {
            toast.error(err?.message || "Không thể thêm giảng viên.");
        }
    };

    // --- EXCEL IMPORT TUTOR ACTIONS ---
    const handleUploadTutor = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const listImported = await PostFormData(`${HOPDONGCHITIET_API.IMPORT_GIANGVIEN}/${contractId}`, formData);
            const rawList = listImported?.$values || listImported || [];

            const checkedList = await Promise.all(
                rawList.map(async (tutor) => {
                    const cccd = tutor.identityCard;
                    const resolvedId = await checkAccountExistsOnSso(cccd);
                    return resolvedId;
                })
            );

            const finalAccountIds = checkedList.filter(Boolean);
            await Post(`${HOPDONGCHITIET_API.SAVE_AND_LINK_TUTORS}/${contractId}`, null, finalAccountIds);
            toast.success("Đã nhập danh sách giảng viên từ file Excel và liên kết thành công!");
            refetchTutors();
        } catch (err) {
            toast.error(err?.message || 'Có lỗi xảy ra trong quá trình import file.');
        } finally {
            if (tutorFileInputRef.current) tutorFileInputRef.current.value = '';
        }
    };

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4" style={{ gap: '12px' }}>
                <div className="mb-2 mb-sm-0">
                    <h5 className="font-weight-bold text-dark mb-1">Giảng viên hướng dẫn thực hành lâm sàng</h5>
                    <div className="text-muted small">Danh sách Roster các bác sĩ/giảng viên tham gia hướng dẫn</div>
                </div>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        style={{ borderRadius: '8px', padding: '8px 16px', whiteSpace: 'nowrap' }}
                        onClick={handleOpenAddTutor} disabled={!contractId}
                    >
                        <i className="fas fa-plus mr-2"></i> Thêm giảng viên
                    </Button>
                    <input
                        type="file"
                        ref={tutorFileInputRef}
                        style={{ display: 'none' }}
                        accept=".xlsx, .xls"
                        onChange={handleUploadTutor}
                    />
                    <Button
                        variant="success"
                        size="sm"
                        style={{ borderRadius: '8px', padding: '8px 16px', whiteSpace: 'nowrap' }}
                        onClick={() => tutorFileInputRef.current?.click()} disabled={!contractId}
                    >
                        <i className="fas fa-file-excel mr-2"></i> Import danh sách giảng viên
                    </Button>
                </div>
            </div>

            {isTutorsLoading ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary spinner-border-sm mr-2"></div> Đang tải danh sách giảng viên...
                </div>
            ) : tutorsData && tutorsData.length > 0 ? (
                <div className="table-responsive rounded-lg border">
                    <Table striped hover className="mb-0" style={{ fontSize: '0.9rem', verticalAlign: 'middle' }}>
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: '60px' }} className="text-center">STT</th>
                                <th>Họ và tên</th>
                                <th>Khoa</th>
                                <th style={{ width: '120px' }} className="text-center">Hành động</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tutorsData.map((tutor, idx) => (
                                <tr key={tutor.id || tutor.Id}>
                                    <td className="text-center text-muted">{idx + 1}</td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <div>
                                                <div className="font-weight-bold text-dark">{tutor.fullName || tutor.FullName}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <TutorDepartmentCell accountId={tutor.accountId || tutor.AccountId} />
                                    </td>
                                    <td className="text-center">
                                        <Button variant="outline-primary" size="sm" className="mr-1" style={{ borderRadius: '6px' }} onClick={() => toast.info('Tính năng sửa đổi thông tin giảng viên đang hoàn thiện.')}>
                                            <i className="fas fa-edit"></i>
                                        </Button>
                                        <Button variant="outline-danger" size="sm" style={{ borderRadius: '6px' }} onClick={() => toast.info('Tính năng xóa giảng viên đang hoàn thiện.')}>
                                            <i className="fas fa-trash-alt"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <div className="text-center py-5 text-muted border rounded-lg bg-light">
                    <i className="fas fa-user-md fa-3x mb-3 text-muted" style={{ opacity: 0.5 }}></i>
                    <h5>Chưa có danh sách người hướng dẫn</h5>
                    <p className="small mb-0">Tải lên file Excel để nhập danh sách giảng viên tham gia hướng dẫn.</p>
                </div>
            )}

            {/* ADD TUTOR MODAL */}
            <Modal show={showAddTutorModal} onHide={() => setShowAddTutorModal(false)} centered>
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="font-weight-bold">Thêm giảng viên hướng dẫn mới</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small font-weight-bold">Họ và tên giảng viên <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Ví dụ: Bác sĩ Nguyễn Văn B..."
                                value={newTutor.fullName}
                                onChange={(e) => setNewTutor({ ...newTutor, fullName: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small font-weight-bold">Số CCCD giảng viên <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Nhập số CCCD để khớp tài khoản trên SSO..."
                                value={newTutor.identityCard}
                                onChange={(e) => setNewTutor({ ...newTutor, identityCard: e.target.value })}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="border-top-0 pt-0">
                    <Button variant="light" onClick={() => setShowAddTutorModal(false)} style={{ borderRadius: '8px' }}>
                        Hủy bỏ
                    </Button>
                    <Button variant="primary" onClick={handleSaveTutor} style={{ borderRadius: '8px' }}>
                        Lưu thông tin
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TutorTab;
export { TutorDepartmentCell };
